/**
 * Стандартный PWA install по документации Chrome / web.dev:
 * 1. beforeinstallprompt → preventDefault(), сохранить событие
 * 2. Клик по кнопке → event.prompt() → системный диалог «Установить KredaFi?»
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type Listener = () => void;

declare global {
  interface Window {
    __pwaInstallEvent?: BeforeInstallPromptEvent | null;
  }
}

const INSTALLED_KEY = "kredafi_pwa_installed";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<Listener>();

function getPrompt(): BeforeInstallPromptEvent | null {
  if (typeof window === "undefined") return null;
  return window.__pwaInstallEvent ?? deferredPrompt;
}

function setPrompt(event: BeforeInstallPromptEvent | null): void {
  deferredPrompt = event;
  if (typeof window !== "undefined") {
    window.__pwaInstallEvent = event;
  }
}

function notify(): void {
  listeners.forEach((fn) => fn());
}

function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Chrome снова предлагает установку — ярлыка на устройстве нет. */
function clearInstalledMarkIfInstallOffered(): void {
  try {
    localStorage.removeItem(INSTALLED_KEY);
  } catch {
    /* приватный режим */
  }
}

export function wasPwaInstalled(): boolean {
  if (typeof window === "undefined") return false;

  if (isStandaloneDisplay()) {
    return true;
  }

  try {
    return localStorage.getItem(INSTALLED_KEY) === "true";
  } catch {
    return false;
  }
}

export function markPwaInstalled(): void {
  try {
    localStorage.setItem(INSTALLED_KEY, "true");
  } catch {
    /* приватный режим */
  }
  setPrompt(null);
  notify();
}

function bind(): void {
  if (typeof window === "undefined") return;

  if (window.__pwaInstallEvent) {
    deferredPrompt = window.__pwaInstallEvent;
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    clearInstalledMarkIfInstallOffered();
    setPrompt(event as BeforeInstallPromptEvent);
    notify();
  });

  window.addEventListener("pwa-install-ready", () => {
    if (window.__pwaInstallEvent) {
      deferredPrompt = window.__pwaInstallEvent;
      notify();
    }
  });

  window.addEventListener("appinstalled", () => {
    markPwaInstalled();
  });

  const displayMode = window.matchMedia("(display-mode: standalone)");
  const onDisplayMode = () => notify();
  if (typeof displayMode.addEventListener === "function") {
    displayMode.addEventListener("change", onDisplayMode);
  } else if (typeof displayMode.addListener === "function") {
    displayMode.addListener(onDisplayMode);
  }
}

bind();

export function canInstallPwa(): boolean {
  return getPrompt() !== null;
}

export function subscribePwaInstall(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Только из onClick. Сразу вызывает prompt() — открывает диалог Chrome. */
export function installPwaFromClick(): void {
  const event = getPrompt();
  if (!event) return;

  setPrompt(null);
  event
    .prompt()
    .then(() => event.userChoice)
    .then(({ outcome }) => {
      if (outcome === "accepted") {
        markPwaInstalled();
      } else {
        notify();
      }
    })
    .catch(() => notify());
}

export function detectInstallPlatform(): "ios" | "mac" | "android" | "desktop" {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  const ios =
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (ios) return "ios";
  if (/Mac/i.test(navigator.platform)) return "mac";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}
